'use client'

import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { fmtAxis, fmtCompact, fmtDollars } from './format'
import type { SavingsPoint } from './types'

// SVG presentation attributes don't support CSS custom properties — map tokens to hex values here.
const C = {
  axis: '#64666a', // --color-neutral-darker
  grid: '#ebebec', // --color-frost
  savings: '#2a8a6e', // --color-green
  savingsFill: '#e3faf2', // --color-green-surface
  need: '#2a2a2a', // --color-charcoal
  tooltipBg: '#154238', // --color-green-darker
  tooltipText: '#f4eee6', // --color-bone
} as const

interface SavingsChartProps {
  points: SavingsPoint[]
  whatYouNeed: number
  retirementAge: number
}

export function SavingsChart({ points, whatYouNeed, retirementAge }: SavingsChartProps) {
  const last = points[points.length - 1]
  const peak = Math.max(whatYouNeed, last?.savings ?? 0)

  return (
    <section
      aria-labelledby="ontrack-chart-heading"
      className="rounded-3xl border border-smoke bg-white p-5 tablet:p-8"
    >
      <h2 id="ontrack-chart-heading" className="text-title-xs text-charcoal">
        Projected savings vs. what you&apos;ll need
      </h2>
      <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-2" aria-hidden="true">
        <li className="flex items-center gap-2 text-body-xs text-neutral-darker">
          <span className="h-1 w-5 rounded-full bg-green" />
          Your projected savings
        </li>
        <li className="flex items-center gap-2 text-body-xs text-neutral-darker">
          <span className="h-0 w-5 border-t-2 border-dashed border-charcoal" />
          What you&apos;ll need at {retirementAge}
        </li>
      </ul>

      <p className="sr-only text-body">
        Your savings are projected to grow to {fmtCompact(last?.savings ?? 0)} by age {retirementAge}, compared with{' '}
        {fmtCompact(whatYouNeed)} needed at retirement.
      </p>

      <div className="mt-4 h-64 tablet:h-72" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={C.grid} vertical={false} />
            <XAxis
              dataKey="age"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: C.axis, fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: C.grid }}
              allowDecimals={false}
            />
            <YAxis
              tickFormatter={fmtAxis}
              tick={{ fill: C.axis, fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={56}
              domain={[0, Math.ceil(peak * 1.1)]}
            />
            <Tooltip
              cursor={{ stroke: C.grid }}
              contentStyle={{ backgroundColor: C.tooltipBg, border: 'none', borderRadius: 12 }}
              labelStyle={{ color: C.tooltipText, fontWeight: 600 }}
              itemStyle={{ color: C.tooltipText }}
              labelFormatter={(label) => `Age ${label}`}
              formatter={(value) => [fmtDollars(Number(value)), 'Projected savings']}
            />
            <Area
              type="monotone"
              dataKey="savings"
              stroke={C.savings}
              strokeWidth={2.5}
              fill={C.savingsFill}
              fillOpacity={1}
              isAnimationActive={false}
            />
            <ReferenceLine y={whatYouNeed} stroke={C.need} strokeDasharray="6 4" strokeWidth={1.5} />
            {last && (
              <ReferenceDot x={last.age} y={last.savings} r={6} fill={C.savings} stroke="#ffffff" strokeWidth={2} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  )
}
